import React, { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import Modal from "../common/modal";
import "../../assets/styles/modal.css";
import { PLAN_LIMIT_FIELDS, buildPlanLimitsPayload } from "../../utils/planLimitsForm";

const PackageModal = ({
  isOpen,
  onClose,
  formData = {},
  setFormData,
  mode = "edit",
  onSave,
  isLoading = false,
}) => {
  const [errors, setErrors] = useState({});
  const [limitValues, setLimitValues] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    setLimitValues({ ...(formData.limits || {}) });
    setErrors({});
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleLimitChange = (field, value) => {
    setLimitValues((prev) => ({
      ...prev,
      [field]: value,
    }));
    setFormData((prev) => ({
      ...prev,
      limits: {
        ...prev.limits,
        [field]: value,
      },
    }));
  };

  const handleAddFeature = () => {
    setFormData((prev) => ({
      ...prev,
      features: [...prev.features, ""],
    }));
  };

  const handleFeatureChange = (index, value) => {
    const updatedFeatures = [...formData.features];
    updatedFeatures[index] = value;
    setFormData((prev) => ({
      ...prev,
      features: updatedFeatures,
    }));
  };

  const handleRemoveFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = "Plan name is required";
    if (!formData.code?.trim()) newErrors.code = "Code is required";
    if (!formData.type) newErrors.type = "Type is required";
    if (formData.price === "" || formData.price === null || formData.price === undefined) {
      newErrors.price = "Price is required";
    }
    if (!formData.billingCycle) newErrors.billingCycle = "Billing cycle is required";
    if (!formData.pricingType) newErrors.pricingType = "Pricing type is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    const limitPayload = buildPlanLimitsPayload(limitValues);

    const payload = {
      name: formData.name.trim(),
      description: formData.description || "",
      code: formData.code.trim(),
      type: formData.type,
      features: (formData.features || []).filter((f) => String(f).trim() !== ""),
      ...limitPayload,
      pricing: [
        {
          price: Number(formData.price),
          offerPrice: formData.offerPrice === "" || formData.offerPrice === null || formData.offerPrice === undefined
            ? 0
            : Number(formData.offerPrice),
          // Backend enum: company | user
          type: formData.pricingType,
          billingCycle: formData.billingCycle,
        },
      ],
    };

    if (onSave) onSave(payload);
  };

  const inputStyle = (field) => ({
    width: "100%",
    padding: "10px",
    border: `1px solid ${errors[field] ? "#dc3545" : "#e0e0e0"}`,
    borderRadius: "6px",
    fontSize: "14px",
    color: "#333",
    boxSizing: "border-box",
    outline: "none",
  });

  const labelStyle = {
    fontSize: "12px",
    color: "#0a0a0a",
    fontWeight: "600",
    display: "block",
    marginBottom: "6px",
  };

  const errorStyle = {
    color: "#dc3545",
    fontSize: "11px",
    marginTop: "4px",
  };

  const fieldWrap = { marginBottom: "16px" };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === "add" ? "Add Package" : "Edit Package"}
      showCloseButton={true}
    >
      <div style={{ padding: "10px 0" }}>
        <div className="package-form-row">
          <div style={fieldWrap}>
            <label style={labelStyle}>Plan Name *</label>
            <input
              type="text"
              value={formData.name || ""}
              placeholder="e.g., Professional"
              onChange={(e) => handleInputChange("name", e.target.value)}
              style={inputStyle("name")}
            />
            {errors.name && <p style={errorStyle}>{errors.name}</p>}
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Plan Code *</label>
            <input
              type="text"
              value={formData.code || ""}
              placeholder="e.g., PRO"
              onChange={(e) => handleInputChange("code", e.target.value)}
              style={inputStyle("code")}
            />
            {errors.code && <p style={errorStyle}>{errors.code}</p>}
          </div>
        </div>

        <div className="package-form-row">
          <div style={fieldWrap}>
            <label style={labelStyle}>Plan Type *</label>
            <select
              value={formData.type || ""}
              onChange={(e) => handleInputChange("type", e.target.value)}
              style={inputStyle("type")}
            >
              <option value="">Select type</option>
              <option value="trial">Trial</option>
              <option value="paid">Paid</option>
            </select>
            {errors.type && <p style={errorStyle}>{errors.type}</p>}
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Billing Cycle *</label>
            <select
              value={formData.billingCycle || ""}
              onChange={(e) => handleInputChange("billingCycle", e.target.value)}
              style={inputStyle("billingCycle")}
            >
              <option value="">Select cycle</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
            {errors.billingCycle && <p style={errorStyle}>{errors.billingCycle}</p>}
          </div>
        </div>

        <div className="package-form-row">
          <div style={fieldWrap}>
            <label style={labelStyle}>Pricing For *</label>
            <select
              value={formData.pricingType || ""}
              onChange={(e) => handleInputChange("pricingType", e.target.value)}
              style={inputStyle("pricingType")}
            >
              <option value="">Select pricing type</option>
              <option value="company">Company</option>
              <option value="user">User</option>
            </select>
            {errors.pricingType && <p style={errorStyle}>{errors.pricingType}</p>}
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Price (AED) *</label>
            <input
              type="number"
              value={formData.price ?? ""}
              placeholder="0"
              min="0"
              onChange={(e) => handleInputChange("price", e.target.value)}
              style={inputStyle("price")}
            />
            {errors.price && <p style={errorStyle}>{errors.price}</p>}
          </div>
        </div>

        <div className="package-form-row">
          <div style={fieldWrap}>
            <label style={labelStyle}>Offer Price (AED)</label>
            <input
              type="number"
              value={formData.offerPrice ?? ""}
              placeholder="0"
              min="0"
              onChange={(e) => handleInputChange("offerPrice", e.target.value)}
              style={inputStyle("offerPrice")}
            />
          </div>
          <div style={fieldWrap} />
        </div>

        <div style={{ ...fieldWrap, marginBottom: "8px" }}>
          <label style={{ ...labelStyle, fontSize: "13px", marginBottom: "10px" }}>
            Plan Limits
          </label>
          <div className="package-form-row">
            {PLAN_LIMIT_FIELDS.map((field) => (
              <div key={field.key} style={fieldWrap}>
                <label style={labelStyle}>{field.label}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={
                    limitValues[field.key] === undefined ||
                    limitValues[field.key] === null
                      ? ""
                      : String(limitValues[field.key])
                  }
                  placeholder={field.placeholder}
                  onChange={(e) => {
                    const next = e.target.value.replace(/[^\d]/g, "");
                    handleLimitChange(field.key, next);
                  }}
                  style={inputStyle(field.key)}
                />
              </div>
            ))}
          </div>
        </div>

        <div style={fieldWrap}>
          <label style={labelStyle}>Description</label>
          <textarea
            value={formData.description || ""}
            placeholder="Enter description"
            rows={3}
            onChange={(e) => handleInputChange("description", e.target.value)}
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #e0e0e0",
              borderRadius: "6px",
              fontSize: "14px",
              color: "#333",
              resize: "vertical",
              boxSizing: "border-box",
              outline: "none",
            }}
          />
        </div>

        <div style={{ marginBottom: "20px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <label style={labelStyle}>Features</label>
            <button
              type="button"
              onClick={handleAddFeature}
              style={{
                background: "none",
                border: "none",
                color: "#5C308D",
                fontSize: "14px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "5px",
                fontWeight: "500",
              }}
            >
              + Add Feature
            </button>
          </div>
          {(formData.features || []).map((feature, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "10px",
              }}
            >
              <input
                type="text"
                value={feature}
                placeholder="Enter feature"
                onChange={(e) => handleFeatureChange(idx, e.target.value)}
                style={{
                  flex: 1,
                  padding: "10px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                  fontSize: "14px",
                  color: "#333",
                  outline: "none",
                }}
              />
              <Trash2
                size={18}
                style={{ color: "#dc3545", cursor: "pointer", flexShrink: 0 }}
                onClick={() => handleRemoveFeature(idx)}
              />
            </div>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "15px",
            marginTop: "10px",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            style={{
              flex: 1,
              padding: "12px",
              border: "1px solid #e0e0e0",
              borderRadius: "6px",
              background: "white",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isLoading}
            style={{
              flex: 1,
              padding: "12px",
              border: "none",
              borderRadius: "6px",
              background: isLoading ? "#a07fc0" : "#5C308D",
              color: "white",
              cursor: isLoading ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            {isLoading ? (
              <>
                <span
                  style={{
                    width: "14px",
                    height: "14px",
                    border: "2px solid rgba(255,255,255,0.4)",
                    borderTopColor: "white",
                    borderRadius: "50%",
                    display: "inline-block",
                    animation: "spin 0.7s linear infinite",
                  }}
                />
                Saving...
              </>
            ) : mode === "add" ? (
              "Create Package"
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Modal>
  );
};

export default PackageModal;
